import { computed, ref } from "vue";
import type { ThemeColorMode, ThemePublicDto, ThemeSettings } from "../types/theme";
import { buildThemeCss } from "../services/theme/themeCss";
import { loadThemeFont } from "../services/theme/themeFonts";
import { themeTL } from "../generated/tl";

/**
 * Vzhled aplikace (docs/tasks/design/plan-001.md). Aktivní téma vloží server do HTML jako
 * `window.__fxTheme` (HomePresenter), takže `initTheme()` ho aplikuje synchronně před vykreslením.
 * Barevný režim: volba uživatele (localStorage) > výchozí režim tématu; "system" sleduje OS.
 */

declare global {
	interface Window { __fxTheme?: ThemePublicDto }
}

const STORAGE_KEY = 'fx-color-mode';
const STYLE_ID = 'fx-theme';

const activeTheme = ref<ThemePublicDto | null>(null);
// co se právě zobrazuje: aktivní téma, nebo rozpracovaný náhled z administrace
const shownTheme = ref<ThemePublicDto | null>(null);
const userMode = ref<ThemeColorMode | null>(readStoredMode());
const systemDark = ref(false);

function readStoredMode(): ThemeColorMode | null {
	try {
		const v = localStorage.getItem(STORAGE_KEY);
		return v === 'light' || v === 'dark' || v === 'system' ? v : null;
	} catch {
		return null;
	}
}

const effectiveDark = computed(() => {
	const mode = userMode.value ?? shownTheme.value?.settings.defaultColorMode ?? 'light';
	return mode === 'dark' || (mode === 'system' && systemDark.value);
});

const applyToDom = () => {
	const theme = shownTheme.value;
	if (!theme) return;
	let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
	if (!style) {
		style = document.createElement('style');
		style.id = STYLE_ID;
		document.head.appendChild(style);
	}
	style.textContent = buildThemeCss(theme.settings, { light: theme.logoLightUrl, dark: theme.logoDarkUrl });
	const root = document.documentElement;
	root.setAttribute('data-bs-theme', effectiveDark.value ? 'dark' : 'light');
	// textura pozadí se v tmavém režimu nepoužívá (je navržená pro světlé pozadí)
	root.setAttribute('data-fx-pattern', theme.settings.backgroundPattern && !effectiveDark.value ? 'on' : 'off');
	void loadThemeFont(theme.settings.fonts.body);
	void loadThemeFont(theme.settings.fonts.heading);
};

const show = (theme: ThemePublicDto) => {
	shownTheme.value = theme;
	applyToDom();
};

let initialized = false;

/** Zavolat jednou v main.ts před vytvořením aplikace. */
export const initTheme = (): void => {
	if (initialized) return;
	initialized = true;
	const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
	systemDark.value = !!mq?.matches;
	mq?.addEventListener?.('change', e => { systemDark.value = e.matches; applyToDom(); });
	if (window.__fxTheme) {
		activeTheme.value = window.__fxTheme;
		show(window.__fxTheme);
	}
};

export function useTheme() {
	const setColorMode = (mode: ThemeColorMode | null) => {
		userMode.value = mode;
		try {
			if (mode === null) localStorage.removeItem(STORAGE_KEY);
			else localStorage.setItem(STORAGE_KEY, mode);
		} catch { /* soukromé okno apod. — volba platí jen do reloadu */ }
		applyToDom();
	};

	/** Přepíná světlý ↔ tmavý podle toho, co se právě zobrazuje. */
	const toggleDark = () => setColorMode(effectiveDark.value ? 'light' : 'dark');

	/** Znovu načte aktivní téma ze serveru (po změně v administraci). */
	const refreshActiveTheme = async () => {
		const theme = await themeTL.getActiveTheme();
		activeTheme.value = theme;
		show(theme);
	};

	/** Okamžitý náhled rozpracovaného nastavení (administrace). */
	const preview = (settings: ThemeSettings, logos: { light: string | null, dark: string | null }) => {
		const base = activeTheme.value;
		show({
			id: base?.id ?? null, name: base?.name ?? '', isActive: false,
			settings, logoLightUrl: logos.light, logoDarkUrl: logos.dark,
		});
	};
	const endPreview = () => { if (activeTheme.value) show(activeTheme.value); };

	const logoUrl = computed(() => {
		const t = shownTheme.value;
		if (!t) return null;
		return effectiveDark.value ? (t.logoDarkUrl ?? t.logoLightUrl) : (t.logoLightUrl ?? t.logoDarkUrl);
	});

	/** Horní lišta je tmavá v obou režimech → preferuje se logo pro tmavé pozadí. */
	const navbarLogoUrl = computed(() => {
		const t = shownTheme.value;
		return t ? (t.logoDarkUrl ?? t.logoLightUrl) : null;
	});

	return {
		activeTheme, shownTheme, isDark: effectiveDark, userMode, logoUrl, navbarLogoUrl,
		setColorMode, toggleDark, refreshActiveTheme, preview, endPreview,
	};
}
